import type { Feature, Polygon } from 'geojson'

type Position = [number, number, ...number[]]
type BBox = [number, number, number, number]

interface AnyGeoJSON {
  type: string
  coordinates?: any
  geometry?: AnyGeoJSON
  geometries?: AnyGeoJSON[]
  features?: AnyGeoJSON[]
}

/**
 * Returns the bounding box [west, south, east, north] of any GeoJSON object.
 */
export function bbox(geojson: AnyGeoJSON): BBox {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  coordEach(geojson, ([x, y]) => {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  })

  return [minX, minY, maxX, maxY]
}

/**
 * Converts a [west, south, east, north] bbox into a GeoJSON Polygon feature.
 */
export function bboxPolygon(box: BBox): Feature<Polygon> {
  const [west, south, east, north] = box
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [west, south],
          [east, south],
          [east, north],
          [west, north],
          [west, south],
        ],
      ],
    },
  }
}

function coordEach(geojson: AnyGeoJSON, callback: (coord: Position) => void) {
  if (!geojson) return

  const geometry =
    geojson.type === 'Feature'
      ? geojson.geometry
      : geojson.type === 'FeatureCollection'
      ? null
      : geojson

  if (geojson.type === 'FeatureCollection') {
    for (const feature of geojson.features!) {
      coordEach(feature, callback)
    }
    return
  }

  if (!geometry) return

  if (geometry.type === 'GeometryCollection') {
    for (const g of geometry.geometries!) {
      coordEach(g, callback)
    }
    return
  }

  const coords = geometry.coordinates
  switch (geometry.type) {
    case 'Point':
      callback(coords)
      break
    case 'MultiPoint':
    case 'LineString':
      for (const c of coords) callback(c)
      break
    case 'MultiLineString':
    case 'Polygon':
      for (const ring of coords) for (const c of ring) callback(c)
      break
    case 'MultiPolygon':
      for (const poly of coords)
        for (const ring of poly) for (const c of ring) callback(c)
      break
  }
}
